import { getDefaultColumnsSettings } from '../TableColumnsSettings';

import { generateZoomInitialTables } from '../../../util/TableTools';
import {
   METRICS_PLAYER,
   LATENCY,
   PLAY_COUNT,
   PLAYTIME,
   BUFFERING_RATIO,
   PLAYER_LOADING,
   H5LIVE_PLAYER_VERSIONS,
   STARTUP_TIME,
   ERROR_CODES,
   STOP_REASONS,
} from '../ZoomConstants';
import { formatTimeNumberInt, format_time_Number, getIntegerWithDecimalSeparator } from '../../../util/NumberFormatter';

export const H5LIVE_SETTINGS = {
   [PLAY_COUNT]: {
      title: (term) => `Playbacks for ${term}`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['playbacks']),
      path: 'h5live/views',
      urlAddon: (term, group) => `&zoomWidget=${group}&zoomMetric=${term}`,
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Playbacks'],
         propNames: ['playbacks'],
      },
      dynamicLabelFn: (label, term) => `${label} for '${term}'`,
      dataFieldNames: ['count', 'uniqueCount'],
      columnNames: ['Total count', 'Playback session count'],
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell), (cell) => getIntegerWithDecimalSeparator(cell)],
      columnsSettings: getDefaultColumnsSettings,
   },
   [LATENCY]: {
      title: () => 'Latency',
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['latencies']),
      path: 'h5live/latency',
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Latency'],
         propNames: ['latencies'],
      },
      dataFieldNames: ['median', 'avg', 'playtime', 'uniqueCount'],
      columnNames: ['Median', 'Average', 'Total Playtime', 'Playback session count'],
      formatters: [
         (cell) => format_time_Number(cell),
         (cell) => format_time_Number(cell),
         (cell) => formatTimeNumberInt(cell),
         (cell) => getIntegerWithDecimalSeparator(cell),
      ],
      columnsSettings: getDefaultColumnsSettings,
   },
   [PLAYTIME]: {
      title: () => 'Playtime',
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['playtimes']),
      path: 'h5live/playtime',
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Playtime'],
         propNames: ['playtimes'],
      },
      dataFieldNames: ['median', 'avg', 'sum', 'uniqueCount'],
      columnNames: ['Median', 'Average', 'Total', 'Playback session count'],
      formatters: [
         (cell) => format_time_Number(cell),
         (cell) => format_time_Number(cell),
         (cell) => format_time_Number(cell),
         (cell) => getIntegerWithDecimalSeparator(cell),
      ],
      columnsSettings: getDefaultColumnsSettings,
   },
   [BUFFERING_RATIO]: {
      title: () => 'Buffering play ratio',
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['ratios']),
      path: 'h5live/bufferingratio',
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Buffering ratio'],
         propNames: ['ratios'],
      },
      dataFieldNames: ['ratio', 'bufferingTime', 'playtime', 'uniqueCount'],
      columnNames: ['Buffering ratio', 'Total buffering time', 'Total playtime', 'Playback session count'],
      formatters: [
         (cell) => `${cell} %`,
         (cell) => formatTimeNumberInt(cell),
         (cell) => formatTimeNumberInt(cell),
         (cell) => getIntegerWithDecimalSeparator(cell),
      ],
      columnsSettings: getDefaultColumnsSettings,
   },
   [PLAYER_LOADING]: {
      title: (term) => `Player loadings for ${term}`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['loadings']),
      path: 'h5live/playerloadings',
      urlAddon: (term) => `&zoomMetric=${term}`,
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Player loadings'],
         propNames: ['loadings'],
      },
      dynamicLabelFn: (label, term) => `${label} for '${term}'`,
      dataFieldNames: ['count', 'uniqueCount'],
      columnNames: ['Total count', 'Playback session count'],
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell), (cell) => getIntegerWithDecimalSeparator(cell)],
      columnsSettings: getDefaultColumnsSettings,
   },
   [H5LIVE_PLAYER_VERSIONS]: {
      title: (term) => `Player version '${term}'`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['versions']),
      path: 'h5live/usedVersions',
      urlAddon: (term) => `&zoomMetric=${term}`,
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Player version'],
         propNames: ['versions'],
      },
      dynamicLabelFn: (label, term) => `${label} '${term}'`,
      dataFieldNames: ['playtime', 'bufferingTime'],
      columnNames: ['Total playtime', 'Total buffering time'],
      formatters: [(cell) => formatTimeNumberInt(cell), (cell) => formatTimeNumberInt(cell)],
      columnsSettings: getDefaultColumnsSettings,
   },
   [STARTUP_TIME]: {
      title: () => 'Startup time',
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['startups']),
      path: 'h5live/startuptime',
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Startup time'],
         propNames: ['startups'],
      },
      dataFieldNames: ['median', 'avg', 'uniqueCount'],
      columnNames: ['Median', 'Average', 'Playback session count'],
      formatters: [
         (cell) => format_time_Number(cell, true),
         (cell) => format_time_Number(cell, true),
         (cell) => getIntegerWithDecimalSeparator(cell),
      ],
      columnsSettings: getDefaultColumnsSettings,
   },
   [ERROR_CODES]: {
      title: (term) => `Error code '${term}'`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['errorcodes']),
      path: 'h5live/errorcodes',
      urlAddon: (term) => `&zoomMetric=${term}`,
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Error code'],
         propNames: ['errorcodes'],
      },
      dynamicLabelFn: (label, term) => `${label} '${term}'`,
      dataFieldNames: ['count', 'uniqueCount'],
      columnNames: ['Total count', 'Playback session count'],
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell), (cell) => getIntegerWithDecimalSeparator(cell)],
      columnsSettings: getDefaultColumnsSettings,
   },
   [STOP_REASONS]: {
      title: (term) => `'${term}' as the stop reason`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['stopreasons']),
      path: 'h5live/stopReasons',
      urlAddon: (term) => `&zoomMetric=${term}`,
      dataAccessFn: (res) => res.data,
      categories: {
         labels: ['Stop reason'],
         propNames: ['stopreasons'],
      },
      dynamicLabelFn: (label, term) => `${label} '${term}'`,
      dataFieldNames: ['count', 'playtime', 'bufferingTime', 'uniqueCount'],
      columnNames: ['Total count', 'Total playtime', 'Total buffering time', 'Playback session count'],
      formatters: [
         (cell) => getIntegerWithDecimalSeparator(cell),
         (cell) => formatTimeNumberInt(cell),
         (cell) => formatTimeNumberInt(cell),
         (cell) => getIntegerWithDecimalSeparator(cell),
      ],
      columnsSettings: getDefaultColumnsSettings,
   },
};
